import useBreakpoint from 'antd/lib/grid/hooks/useBreakpoint';
import PencilPdfIcon from 'assets/Icons/PencilPdfIcon';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useRecoilState, useRecoilValue } from 'recoil';
import { UAParser } from 'ua-parser-js';

import { ITakeNodeModal } from '@/components/PdfReader';
import { useUIControl } from '@/components/PdfReader/useUIControl';
import ButtonDefault, {
  ButtonVariants,
} from '@/components/shared/basic/button';

import { aiModalOpenAtom } from '@/atoms/pdfStates/aiModalOpenAtom';
import { isTextSelectingAtom } from '@/atoms/pdfStates/isTextSelectingAtom';
import { pageNumberAtom } from '@/atoms/pdfStates/pageNumber';

const isBrowser = typeof window !== 'undefined';
const parser = new UAParser();
const device = parser.getDevice();
const os = parser.getOS();

export const isIphone = (): boolean =>
  device.model === 'iPhone' || (device.model?.includes('iPad') ?? false);

export const isMobile = (): boolean =>
  device.type === 'mobile' ||
  os.name === 'Android' ||
  device.model === 'iPhone' ||
  device.model?.includes('iPad') ||
  device.type === 'tablet';

const getTextLayerAncestor = (node: Node | null): HTMLElement | null => {
  let el: Element | null =
    node instanceof Element ? node : (node?.parentElement ?? null);
  while (el) {
    if (el.classList?.contains('react-pdf__Page__textContent'))
      return el as HTMLElement;
    el = el.parentElement;
  }
  return null;
};

const isInViewport = (rect: DOMRect) => {
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <=
      (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
};

type SelectionTooltipProps = {
  ignoreRefs?: React.RefObject<HTMLElement>[];
  parentRef: React.RefObject<HTMLElement>;
  setTakeNodeModal: (value: ITakeNodeModal) => void;
  takeNoteModal: ITakeNodeModal;
};

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(v, max));

function getRelativeTooltipPosition(
  selectionRect: DOMRect,
  tooltipSize: { width: number; height: number },
  pdfContainerRef: React.RefObject<HTMLElement>,
  offsetY = 8,
) {
  const container = pdfContainerRef?.current;
  if (!container) return { top: 0, left: 0 };

  const containerRect = container.getBoundingClientRect();

  let top =
    selectionRect.bottom - containerRect.top + container.scrollTop + offsetY;

  let left =
    selectionRect.left -
    containerRect.left +
    container.scrollLeft +
    selectionRect.width / 2 -
    tooltipSize.width / 2;

  left = clamp(left, 0, container.clientWidth - tooltipSize.width);
  top = clamp(top, 0, container.clientHeight - tooltipSize.height);

  return { top, left };
}

const SelectionTooltip: React.FC<SelectionTooltipProps> = ({
  ignoreRefs = [],
  parentRef,
  setTakeNodeModal,
  takeNoteModal,
}) => {
  const pageNumberMobile = useRecoilValue(pageNumberAtom);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const screens = useBreakpoint();
  const [, setIsTextSelecting] = useRecoilState(isTextSelectingAtom);
  const openDrawer = useRecoilValue<boolean>(aiModalOpenAtom);
  const { onClearOverlay } = useUIControl();

  const [tooltipSize, setTooltipSize] = useState({ width: 160, height: 42 });
  const [showDelayed, setShowDelayed] = useState(false);

  const isInsideIgnoredRefs = (node: Node | null) => {
    let current: Node | null = node;
    while (current) {
      if (ignoreRefs.some((ref) => ref.current?.contains(current))) return true;
      current = current.parentNode;
    }
    return false;
  };

  const clearSelection = useCallback(() => {
    if (isMobile()) {
      return;
    }

    const selection = window.getSelection?.();
    if (selection?.rangeCount) selection.removeAllRanges();
    if (screens.lg) setIsTextSelecting(false);
    setTakeNodeModal({
      value: false,
      pageNumber: 0,
      selectedText: '',
      selectionRect: null,
    });
    setShowDelayed(false);
  }, [setTakeNodeModal]);

  useEffect(() => {
    onClearOverlay(() => clearSelection());
  }, [onClearOverlay, clearSelection]);

  const getSelectedPageNumber = () => {
    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) return null;

    const anchorNode = selection.anchorNode;
    if (!anchorNode) return null;

    let el: Node | null = anchorNode;

    while (el) {
      if (
        el instanceof HTMLElement &&
        el.classList.contains('react-pdf__Page')
      ) {
        return +(el.getAttribute('data-page-number') || 0);
      }
      el = el.parentNode;
    }

    return null;
  };

  const handleTakeNoteClick = () => {
    let pageNumber = getSelectedPageNumber();

    if (isMobile() && pageNumberMobile) {
      pageNumber = pageNumberMobile;
    }

    const selection = window.getSelection();
    if (selection && selection.rangeCount > 0) {
      selection.removeAllRanges();
    }

    setTakeNodeModal({
      ...takeNoteModal,
      value: true,
      pageNumber: pageNumber || 1,
      selectedText: takeNoteModal.selectedText,
    });

    if (screens.lg) setIsTextSelecting(false);
  };

  const tryShowTooltip = useCallback(() => {
    const selection = window.getSelection?.();
    if (!selection || !selection.rangeCount) {
      clearSelection();
      return;
    }

    const selectedText = selection.toString().trim();
    if (!selectedText || selectedText.length > 1200) {
      clearSelection();
      return;
    }

    const range = selection.getRangeAt(0);
    const rect = range.getBoundingClientRect();
    const startNode = range.startContainer;

    if (
      !document.body.contains(startNode) ||
      rect.width === 0 ||
      rect.height === 0 ||
      !isInViewport(rect) ||
      isInsideIgnoredRefs(startNode) ||
      (parentRef?.current && !parentRef.current.contains(startNode)) ||
      !getTextLayerAncestor(startNode)
    ) {
      clearSelection();
      return;
    }

    if (screens.lg) setIsTextSelecting(true);

    setTakeNodeModal({
      ...takeNoteModal,
      selectedText,
      selectionRect: rect,
    });

    setShowDelayed(false);
    if (isMobile()) {
      setTimeout(() => setShowDelayed(true), 400);
    } else {
      setShowDelayed(true);
    }
  }, [
    clearSelection,
    getTextLayerAncestor,
    isInsideIgnoredRefs,
    parentRef,
    screens.lg,
    setIsTextSelecting,
    setTakeNodeModal,
    takeNoteModal,
  ]);

  const handleGlobalSelection = useCallback(
    (event: any) => {
      if (event.type.startsWith('touch')) {
        setTimeout(tryShowTooltip, 100);
      } else {
        tryShowTooltip();
      }
    },
    [tryShowTooltip],
  );

  useEffect(() => {
    if (!isBrowser || takeNoteModal.value || openDrawer) return;
    document.addEventListener('mouseup', handleGlobalSelection);
    document.addEventListener('touchend', handleGlobalSelection);
    return () => {
      document.removeEventListener('mouseup', handleGlobalSelection);
      document.removeEventListener('touchend', handleGlobalSelection);
    };
  }, [handleGlobalSelection, takeNoteModal.value, openDrawer]);

  useEffect(() => {
    if (!isBrowser || takeNoteModal.value || !isMobile) return;

    let selectionTimer: ReturnType<typeof setTimeout> | null = null;
    const handleSelectionChange = () => {
      if (selectionTimer) clearTimeout(selectionTimer);
      selectionTimer = setTimeout(() => {
        const selection = window.getSelection();
        if (selection && selection.toString().trim().length > 0) {
          tryShowTooltip();
        }
      }, 400);
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      if (selectionTimer) clearTimeout(selectionTimer);
    };
  }, [takeNoteModal.value, tryShowTooltip]);

  useEffect(() => {
    if (!isBrowser) return;
    const handleDismiss = () => clearSelection();
    const handleKey = (e: KeyboardEvent) =>
      e.key === 'Escape' && handleDismiss();

    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleDismiss, { passive: true });
    window.addEventListener('resize', handleDismiss, { passive: true });

    return () => {
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleDismiss);
      window.removeEventListener('resize', handleDismiss);
    };
  }, [clearSelection]);

  useEffect(() => {
    if (!isBrowser || takeNoteModal.value || openDrawer) return;
    const handleClickOutside = (e: Event) => {
      const target = e.target as Node;
      const inTooltip = tooltipRef.current?.contains(target);
      const inIgnored = isInsideIgnoredRefs(target);
      if (!inTooltip && !inIgnored) clearSelection();
    };
    document.addEventListener('mousedown', handleClickOutside, {
      capture: true,
    });
    document.addEventListener('touchstart', handleClickOutside, {
      capture: true,
    });
    return () => {
      document.removeEventListener('mousedown', handleClickOutside, {
        capture: true,
      });
      document.removeEventListener('touchstart', handleClickOutside, {
        capture: true,
      });
    };
  }, [clearSelection, takeNoteModal.value, openDrawer]);

  useLayoutEffect(() => {
    if (tooltipRef.current) {
      setTooltipSize({
        width: tooltipRef.current.offsetWidth,
        height: tooltipRef.current.offsetHeight,
      });
    }
  }, [takeNoteModal.selectionRect, takeNoteModal.selectedText, showDelayed]);

  useEffect(() => {
    if (!isMobile || takeNoteModal.value) return;

    let timeout: NodeJS.Timeout | null = null;

    const handleLongPress = () => {
      timeout = setTimeout(
        () => {
          const selection = window.getSelection();
          if (selection && selection.toString().trim().length > 0) {
            tryShowTooltip();
          }
        },
        !screens.lg ? 600 : 300,
      );
    };

    const clearLongPress = () => {
      if (timeout) clearTimeout(timeout);
      timeout = null;
    };

    document.addEventListener('touchstart', handleLongPress);
    document.addEventListener('touchend', clearLongPress);
    document.addEventListener('touchmove', clearLongPress);

    return () => {
      document.removeEventListener('touchstart', handleLongPress);
      document.removeEventListener('touchend', clearLongPress);
      document.removeEventListener('touchmove', clearLongPress);
    };
  }, [tryShowTooltip, takeNoteModal.value]);

  if (
    !takeNoteModal.selectionRect ||
    !takeNoteModal.selectedText ||
    !showDelayed
  )
    return null;

  const { top, left } = getRelativeTooltipPosition(
    takeNoteModal.selectionRect,
    tooltipSize,
    parentRef,
    8,
  );

  return (
    <div
      ref={tooltipRef}
      className='exclude-panning selection-tooltip'
      role='tooltip'
      aria-live='polite'
      style={{
        position: 'absolute',
        top,
        left,
        zIndex: 999,
        pointerEvents: 'auto',
        width: tooltipSize.width,
        maxWidth: 220,
        minWidth: 110,
        boxSizing: 'border-box',
        transition: 'top 0.05s,left 0.05s',
        userSelect: 'none',
        touchAction: 'manipulation',
        WebkitTouchCallout: 'none',
        WebkitUserSelect: 'none',
      }}
    >
      <ButtonDefault
        size={0}
        variant={ButtonVariants.PRIMARY}
        onClick={handleTakeNoteClick}
        onTouchEnd={(e) => {
          e.stopPropagation();
          handleTakeNoteClick();
        }}
        className='w-min'
      >
        <span className='flex text-xs gap-x-2 items-center lg:text-sm py-1 px-2 lg:py-2 whitespace-nowrap'>
          <PencilPdfIcon width={screens.lg ? '24' : '16'} /> Take a Note
        </span>
      </ButtonDefault>
    </div>
  );
};

export default React.memo(SelectionTooltip);
